import React, { useState, useEffect, useRef } from "react";
import {
  DatePicker,
  Select,
  Table,
  Card,
  Row,
  Col,
  Button,
  Space,
  message,
  Layout,
  Input,
  Modal,
} from "antd";
import {
  FileTextOutlined,
  FilterOutlined,
  SearchOutlined,
  EditOutlined,
  EyeOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { getTypeInfraction } from "../api/typeInfraction/infractionType";
import { searchPayementByFilter, getSubscriptionPaymentsByZone } from "../api/payments/paymentRequest";
import { getAgentsForFilter } from "../api/constants";
import { getPaymentMethodInFrench, PAYMENT_METHODS } from "../utils/payment";
import { useSelector } from "react-redux";
import EditPaymentModal from "../components/EditPaymentModal";
import PermissionRender from "../components/PermissionRender";
import { getClientDisplayName, SOURCE_TYPES } from "../utils/constants";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Content } = Layout;

function PaymentPage() {
  const [payments, setPayments] = useState([]);
  const [typeInfractions, setTypeInfractions] = useState([]);
  const [users, setUsers] = useState([]);

  const [searchReference, setSearchReference] = useState("");
  const [dateRange, setDateRange] = useState([]);
  const [selectedType, setSelectedType] = useState("");
  const [loading, setLoading] = useState(false);

  const [selectedUser, setSelectedUser] = useState("");

  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const user = useSelector((state) => state.user.user);

  // Edit payment modal state
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [searchMedthod, setSearchMethod] = useState("");
  const [selectedSource, setSelectedSource] = useState("");
  
  const [selectedPaymentType, setSelectedPaymentType] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");

  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  // Zone export modal state
  const [zoneExportModalVisible, setZoneExportModalVisible] = useState(false);
  const [zoneExportMonth, setZoneExportMonth] = useState(null);
  const [zoneExportPaymentMethod, setZoneExportPaymentMethod] = useState("");
  const [zoneExportLoading, setZoneExportLoading] = useState(false);

  const workerPdfRef = useRef(null);
  const workerExcelRef = useRef(null);

  useEffect(() => {
    workerPdfRef.current = new Worker(
      new URL("../workers/pdfWorker.js", import.meta.url)
    );
    workerExcelRef.current = new Worker(
      new URL("../workers/excelWorker.js", import.meta.url)
    );

    workerPdfRef.current.onmessage = (e) => {
      const { status, blobURL, operation, filename, message } = e.data;

      // Handle different status updates
      switch (status) {
        case "starting":
          console.log("PDF generation starting...");
          break;
        case "generating":
          console.log("Creating PDF document...");
          // You could update a more granular status message here if needed
          break;
        case "complete":
          console.log("PDF generation complete");

          if (operation === "download") {
            const link = document.createElement("a");
            link.href = blobURL;
            link.download = filename || "file.pdf";
            link.click();
            URL.revokeObjectURL(blobURL);
          } else if (operation === "open") {
            window.open(blobURL, "_blank");
          }

          break;
        case "error":
          console.error("PDF generation error:", message);
          // message.error(`Erreur lors de la génération du PDF: ${message}`);
          break;
        default:
          console.warn("Unknown status:", status);
          break;
      }
    };

    workerExcelRef.current.onmessage = (e) => {
      const { success, blobURL, filename, error } = e.data;

      if (success) {
        const link = document.createElement("a");
        link.href = blobURL;
        link.download = filename || "file.xlsx";
        link.click();
        URL.revokeObjectURL(blobURL);
      } else {
        message.error(
          `Erreur lors de la génération du fichier Excel: ${error}`
        );
      }
    };

    return () => {
      workerPdfRef.current?.terminate();
      workerExcelRef.current?.terminate();
    };
  }, []);

  useEffect(() => {
    fetchTypeInfractions();
    fetchUsers();
  }, []);

  useEffect(() => {
    filterPayments(); // Fetch data initially
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filterPayments = async () => {
    setLoading(true);
    try {
      const filters = {
        reference: searchReference,
        type_infraction_id: selectedType,
        start_date:
          dateRange && dateRange[0]
            ? dateRange[0].format("YYYY-MM-DD HH:mm:ss")
            : null,
        end_date:
          dateRange && dateRange[1]
            ? dateRange[1].format("YYYY-MM-DD HH:mm:ss")
            : null,
        id_agent: selectedUser,
        payment_method: searchMedthod,
        source: selectedSource,
        payment_type: selectedPaymentType,
        min_amount: minAmount ? parseFloat(minAmount) : null,
        max_amount: maxAmount ? parseFloat(maxAmount) : null,
      };
      const data = await searchPayementByFilter(filters);
      setPayments(data); // Update state with the fetched data
    } catch (error) {
      message.error("Failed to fetch payments");
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTypeInfractions = async () => {
    setLoading(true);
    try {
      const data = await getTypeInfraction();
      setTypeInfractions(data); // Update state with the fetched data
    } catch (error) {
      message.error("Failed to fetch type  infractions");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await getAgentsForFilter();
      setUsers(data); // Update state with the fetched data
    } catch (error) {
      message.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  // Handle edit payment
  const handleEditPayment = (payment) => {
    setSelectedPayment(payment);
    setEditModalVisible(true);
  };

  const handleEditModalCancel = () => {
    setEditModalVisible(false);
    setSelectedPayment(null);
  };

  const handleEditModalSuccess = () => {
    setEditModalVisible(false);
    setSelectedPayment(null);
    // Refresh the payments list
    filterPayments();
  };

  const columns = [
    {
      title: "Référence",
      dataIndex: "reference",
      key: "reference",
    },
    {
      title: "Payeur",
      dataIndex: "id_agent",
      key: "id_agent",
      render: (_, record) => record.user?.full_name,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (_, record) => {
        return (
          <>
            {record.subscription_id && "Abonnement"}
            {record.infraction_id && "Infraction"}
          </>
        );
      },
    },
    {
      title: "Client",
      dataIndex: ["subscription", "client"],
      key: "client",
      render: (_, record) => (
          <span>{getClientDisplayName(record.subscription?.client) || "—"}</span>
      ),
    },
    {
      title: "N° Contrat",
      dataIndex: ["subscription", "contract_number"],
      key: "contract_number",
      render: (_, record) => record.subscription?.contract_number || "-",
    },

    {
      title: "Méthode de Paiement",
      dataIndex: "payment_method",
      key: "payment_method",
      render: (txt) => getPaymentMethodInFrench(txt),
    },
    {
      title: "Référence de paiement",
      dataIndex: "reference_number",
      key: "reference_number",
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      render: (text) => SOURCE_TYPES[text] || text,
    },
    {
      title: "Montant",
      dataIndex: "amount",
      key: "amount",
    },
    {
      title: "Date et Heure",
      dataIndex: "payment_date",
      key: "payment_date",
    },

    {
      title: "Action",
      key: "action",
      fixed: "right",
      render: (_, record) => {
        const canEdit = record.source === "agent";

        return (
          <Space>
            {record.infraction_id && (
              <Button
                type="link"
                onClick={() =>
                  window.open(`/infraction/${record.infraction_id}`, "_blank")
                }
                icon={<EyeOutlined />}
              ></Button>
            )}

            {record.subscription_id && (
              <Button
                type="link"
                onClick={() =>
                  window.open(
                    `/subscriptions/${record.subscription_id}`,
                    "_blank"
                  )
                }
                icon={<EyeOutlined />}
              ></Button>
            )}

            {canEdit && (
              <PermissionRender requiredPermission={["payment", "update"]}>
                <Button
                  type="link"
                  icon={<EditOutlined />}
                  onClick={() => handleEditPayment(record)}
                >
                
                </Button>
              </PermissionRender>
            )}
          </Space>
        );
      },
    },
  ];

  const handleExcelClick = async () => {
    setIsExportingExcel(true);
    try {
      const timestamp = new Date().toISOString().replace(/:/g, "-");
      const filename = `Paiements_${timestamp}.xlsx`;
      const baseUrl = window.location.origin;

      // Prepare only the necessary data to reduce processing overhead
      const formattedData = payments.map((it) => {
        const client = it.subscription?.client;
        const clientName = client
          ? `${client.first_name || ""} ${client.last_name || ""}`.trim()
          : "";
        return {
          reference: it.reference,
          user_name: it.user?.full_name,
          type_infraction_name: it.subscription_id
            ? "Abonnement"
            : it.infraction_id
            ? "Infraction"
            : "",
          client_name: clientName,
          contract_number: it.subscription?.contract_number || "",
          payment_method: getPaymentMethodInFrench(it.payment_method),
          reference_number: it.reference_number,
          amount: it.amount,
          payment_date: it.payment_date,
          source: SOURCE_TYPES[it.source] || it.source,
          subscription_type: it.subscription?.subscription_type?.name || "",
          zone_name:  it.infraction_id ? it.infraction?.zone?.name : it.subscription_id ? it.subscription?.zone_name : "",
          type_infraction: it.infraction_id ? it.infraction?.type_infraction?.name : "",
        };
      });

      const data = [
        ["Paiements", ``, ``, ``],
        [],
        [],
        user.full_name,
        [],
        [
          [
            {
              text: "Référence",
              type: "string",
              key: "reference",
            },
            {
              text: "Payeur",
              type: "string",
              key: "user_name",
            },
            {
              text: "Type",
              type: "string",
              key: "type_infraction_name",
            },
            {
              text: "Client",
              type: "string",
              key: "client_name",
            },
            {
              text: "N° Contrat",
              type: "string",
              key: "contract_number",
            },
            {
              text: "Contrat",
              type: "string",
              key: "zone_name",
            },
            {
              text: "Type d'Infraction",
              type: "string",
              key: "type_infraction",
            },
            {
              text: "Formule d'Abonnement",
              type: "string",
              key: "subscription_type",
            },
            {
              text: "Méthode de Paiement",
              type: "string",
              key: "payment_method",
            },
            {
              text: "Référence de paiement",
              type: "string",
              key: "reference_number",
            },
            {
              text: "Source",
              type: "string",
              key: "source",
            },
            {
              text: "Montant",
              type: "int",
              key: "amount",
            },

            {
              text: "Date et Heure",
              type: "string",
              key: "payment_date",
            },
          ],
        ],
        [formattedData],
        baseUrl,
      ];

      // Post message to worker
      workerExcelRef.current.postMessage({ data, filename });
    } catch (error) {
      message.error("Erreur lors de la génération du fichier Excel");
    } finally {
      setIsExportingExcel(false);
    }
  };

  const handleZoneExport = async () => {
    if (!zoneExportMonth) {
      message.error("Veuillez sélectionner un mois");
      return;
    }

    const month = zoneExportMonth.month() + 1;
    const year = zoneExportMonth.year();

    setZoneExportLoading(true);
    try {
      const data = await getSubscriptionPaymentsByZone({
        month,
        year,
        payment_method: zoneExportPaymentMethod || undefined,
      });

      if (!data || data.length === 0) {
        message.warning("Aucun paiement trouvé pour les critères sélectionnés");
        return;
      }

      const timestamp = new Date().toISOString().replace(/:/g, "-");
      const filename = `Paiements_Abonnements_Zone_${month}_${year}_${timestamp}.xlsx`;
      const baseUrl = window.location.origin;

      const formattedData = data.map((it) => ({
        reference: it.reference,
        client_name: it.client_name,
        zone_name: it.zone_name,
        contract_number: it.contract_number,
        payment_method: getPaymentMethodInFrench(it.payment_method),
        amount: it.amount,
        payment_date: it.payment_date,
        subscription_type: it.subscription_type,
      }));

      const excelData = [
        [
          "Paiements Abonnements par Zone",
          ``,
          `Mois: ${month}/${year}`,
          zoneExportPaymentMethod
            ? `Méthode: ${getPaymentMethodInFrench(zoneExportPaymentMethod)}`
            : "",
        ],
        [],
        [],
        user.full_name,
        [],
        [
          [
            { text: "Référence", type: "string", key: "reference" },
            { text: "Client", type: "string", key: "client_name" },
            { text: "Contrat", type: "string", key: "zone_name" },
            { text: "Formule d'Abonnement", type: "string", key: "subscription_type" },
            { text: "N° Contrat", type: "string", key: "contract_number" },
            { text: "Méthode de Paiement", type: "string", key: "payment_method" },
            { text: "Montant", type: "int", key: "amount" },
            { text: "Date de Paiement", type: "string", key: "payment_date" },
          ],
        ],
        [formattedData],
        baseUrl,
      ];

      workerExcelRef.current.postMessage({ data: excelData, filename });
      setZoneExportModalVisible(false);
      setZoneExportMonth(null);
      setZoneExportPaymentMethod("");
    } catch (error) {
      message.error("Échec de la récupération des paiements par zone");
    } finally {
      setZoneExportLoading(false);
    }
  };

  return (
    <Layout className="min-h-screen ">
      <Content className="py-6 sm:px-6 lg:px-8">
        <Card className="mb-6 shadow-sm">
          <Space direction="vertical" size="middle" className="w-full">
            <Row gutter={16} align="middle">
              <Col xs={24} sm={12} md={4}>
                <Input
                  placeholder="Rechercher par Référence"
                  prefix={<SearchOutlined />}
                  value={searchReference}
                  onChange={(e) => setSearchReference(e.target.value)}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <RangePicker
                  onChange={(dates) => setDateRange(dates)}
                  className="w-full"
                  showTime={{ format: "HH:mm" }}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Select
                  placeholder="Filtrer par infraction Type"
                  style={{ width: "100%" }}
                  onChange={(value) => setSelectedType(value)}
                  allowClear
                >
                  {/* Populate this with options as needed */}
                  {typeInfractions.map((type) => (
                    <Option key={type.id} value={type.id}>
                      {type.name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Select
                  showSearch
                  placeholder="Filtrer par Constateur"
                  style={{ width: "100%" }}
                  onChange={(value) => setSelectedUser(value)}
                  allowClear
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    String(option?.children)
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {users.map((type) => (
                    <Option key={type.id_agent} value={type.id_agent}>
                      {type?.full_name}
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>
            <Row gutter={16} align="middle">
              <Col xs={24} sm={12} md={4}>
                <Select
                  placeholder="Type de Paiement"
                  style={{ width: "100%" }}
                  onChange={(value) => setSelectedPaymentType(value)}
                  allowClear
                >
                  <Option value="subscription">Abonnement</Option>
                  <Option value="infraction">Infraction</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Input
                  type="number"
                  placeholder="Montant Min"
                  value={minAmount}
                  onChange={(e) => setMinAmount(e.target.value)}
                />
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Input
                  type="number"
                  placeholder="Montant Max"
                  value={maxAmount}
                  onChange={(e) => setMaxAmount(e.target.value)}
                />
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Select
                  placeholder="Filtrer par Méthode de Paiement"
                  style={{ width: "100%" }}
                  onChange={(value) => setSearchMethod(value)}
                  allowClear
                >
                  {/* Populate this with options as needed */}
                  {Object.entries(PAYMENT_METHODS).map(([key, value]) => (
                    <Option key={key} value={key}>
                      {value}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Select
                  placeholder="Filtrer par Source"
                  style={{ width: "100%" }}
                  onChange={(value) => setSelectedSource(value)}
                  allowClear
                >
                  {/* Populate this with options as needed */}
                  {Object.entries(SOURCE_TYPES).map(([key, value]) => (
                    <Option key={key} value={key}>
                      {value}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Button
                  type="primary"
                  icon={<FilterOutlined />}
                  onClick={filterPayments}
                  loading={loading}
                  className="w-full"
                >
                  Rechercher
                </Button>
              </Col>
            </Row>
          </Space>
        </Card>

        <Card className="shadow-sm">
          <div style={{ marginBottom: 16 }} className="flex justify-end">
            <Button
              icon={<DownloadOutlined />}
              onClick={() => setZoneExportModalVisible(true)}
              className="hover:border-blue-700 hover:text-blue-500 mr-4"
            >
              Export Abonnements/Zone
            </Button>
            <Button
              icon={<FileTextOutlined />}
              onClick={async () => await handleExcelClick()}
              className="hover:border-green-700 hover:text-green-500 mr-4"
              disabled={isExportingExcel}
            >
              Exporter Excel
            </Button>
          </div>

          <Table
            dataSource={payments}
            columns={columns}
            rowKey="id"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} sur ${total} paiements`,
              onChange: (page, pageSize) =>
                setPagination({ current: page, pageSize }),
            }}
            scroll={{ x: 'max-content' }}
            loading={loading}
          />
        </Card>
      </Content>

      <EditPaymentModal
        visible={editModalVisible}
        onCancel={handleEditModalCancel}
        onSuccess={handleEditModalSuccess}
        payment={selectedPayment}
      />

      <Modal
        title="Export Paiements Abonnements par Zone"
        open={zoneExportModalVisible}
        onCancel={() => {
          setZoneExportModalVisible(false);
          setZoneExportMonth(null);
          setZoneExportPaymentMethod("");
        }}
        onOk={handleZoneExport}
        okText="Exporter"
        cancelText="Annuler"
        confirmLoading={zoneExportLoading}
        okButtonProps={{ icon: <DownloadOutlined /> }}
      >
        <Space direction="vertical" size="middle" className="w-full" style={{ marginTop: 16 }}>
          <div>
            <label className="block mb-1 font-medium">Mois *</label>
            <DatePicker
              picker="month"
              value={zoneExportMonth}
              onChange={(date) => setZoneExportMonth(date)}
              className="w-full"
              placeholder="Sélectionner le mois"
            />
          </div>
          <div>
            <label className="block mb-1 font-medium">Méthode de Paiement</label>
            <Select
              placeholder="Toutes les méthodes"
              style={{ width: "100%" }}
              value={zoneExportPaymentMethod || undefined}
              onChange={(value) => setZoneExportPaymentMethod(value)}
              allowClear
            >
              {Object.entries(PAYMENT_METHODS).map(([key, value]) => (
                <Option key={key} value={key}>
                  {value}
                </Option>
              ))}
            </Select>
          </div>
        </Space>
      </Modal>
    </Layout>
  );
}

export default PaymentPage;
